import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Search, SlidersHorizontal, Loader2, ArrowDownUp, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 48;

const SORTS = {
  "name-asc": "Name: A to Z",
  "name-desc": "Name: Z to A",
  "price-asc": "Price: Low to High",
  "price-desc": "Price: High to Low",
};

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [search, setSearch] = useState(params.get("search") || "");
  const [debounced, setDebounced] = useState(search);
  const [sort, setSort] = useState("name-asc");
  const [level, setLevel] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => { setLevel(""); setPage(1); }, [category]);
  useEffect(() => { setPage(1); }, [debounced, sort, level]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get("/categories").then((r) => r.data),
  });

  const isEdu = category === "educational-books";

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["products", category, debounced, sort, level, page],
    queryFn: async () => {
      const r = await api.get("/products", {
        params: {
          category: category || undefined,
          search: debounced || undefined,
          level: isEdu && level ? level : undefined,
          sort,
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
        },
      });
      return { items: r.data, total: Number(r.headers["x-total-count"]) || r.data.length };
    },
    placeholderData: keepPreviousData,
  });

  const products = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, total);

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("category", slug);
    else next.delete("category");
    setParams(next);
  };

  const activeCat = categories.find((c) => c.slug === category);

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
      <div className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-brand-blue">
          {activeCat ? activeCat.name : "Our Shop"}
        </h1>
        <p className="mt-2 text-brand-black/60">
          {activeCat ? activeCat.description : "Browse everything we have in store."}
        </p>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-blue/50" />
        <input
          data-testid="shop-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search books, stationery, instruments..."
          className="w-full pl-11 pr-4 py-3 border-2 border-black rounded-md bg-white shadow-brutal focus:outline-none focus:ring-2 focus:ring-brand-gold"
        />
      </div>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-black/60 mr-1">
            <SlidersHorizontal className="w-4 h-4" /> Filter:
          </span>
          <button
            onClick={() => setCategory("")}
            data-testid="filter-all"
            className={`px-4 py-2 text-sm font-semibold border-2 border-black rounded-md transition-all ${
              !category ? "bg-brand-blue text-white shadow-brutal" : "bg-white hover:bg-brand-cream"
            }`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.slug)}
              data-testid={`filter-${c.slug}`}
              className={`px-4 py-2 text-sm font-semibold border-2 border-black rounded-md transition-all ${
                category === c.slug ? "bg-brand-blue text-white shadow-brutal" : "bg-white hover:bg-brand-cream"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <ArrowDownUp className="w-4 h-4 text-brand-black/60" />
          <label htmlFor="sort-select" className="text-sm font-semibold text-brand-black/60">Sort:</label>
          <select
            id="sort-select"
            data-testid="shop-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2 text-sm font-semibold border-2 border-black rounded-md bg-white shadow-brutal cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-gold"
          >
            {Object.entries(SORTS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {isEdu && (
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="level-filter">
          <span className="text-sm font-semibold text-brand-black/60 mr-1">Class level:</span>
          {[["", "All levels"], ["primary", "Primary"], ["secondary", "Secondary (JSS/SSS)"]].map(([val, lbl]) => (
            <button
              key={val || "all"}
              onClick={() => setLevel(val)}
              data-testid={`level-${val || "all"}`}
              className={`px-4 py-2 text-sm font-semibold border-2 border-black rounded-md transition-all ${
                level === val ? "bg-brand-gold text-black shadow-brutal" : "bg-white hover:bg-brand-cream"
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>
      )}

      {!isLoading && total > 0 && (
        <p className="mb-5 text-sm text-brand-black/50" data-testid="results-count">
          Showing {startIdx}–{endIdx} of {total} {total === 1 ? "item" : "items"}
        </p>
      )}

      {isLoading ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-blue" /></div>
      ) : total === 0 ? (
        <div className="py-24 text-center border-2 border-dashed border-black/20 rounded-lg" data-testid="no-products">
          <p className="font-display font-bold text-xl text-brand-blue">No products found</p>
          <p className="text-brand-black/50 mt-2">Try a different category or search term.</p>
        </div>
      ) : (
        <>
          <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 transition-opacity ${isFetching ? "opacity-60" : ""}`} data-testid="products-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2 flex-wrap" data-testid="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="page-prev"
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold border-2 border-black rounded-md bg-white disabled:opacity-40 hover:bg-brand-cream transition-all"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  data-testid={`page-${n}`}
                  className={`min-w-[40px] px-3 py-2 text-sm font-semibold border-2 border-black rounded-md transition-all ${
                    n === page ? "bg-brand-blue text-white shadow-brutal" : "bg-white hover:bg-brand-cream"
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="page-next"
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold border-2 border-black rounded-md bg-white disabled:opacity-40 hover:bg-brand-cream transition-all"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
